import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { Checkbox } from "../../../components/ui/Checkbox";
import { useDispatch, useSelector } from "react-redux";
import { getAreaByGovService } from "redux/services/general_service";
import ProductImageUpload from "./ProductImageUpload";
import Textarea from "components/ui/Textarea";

const ProductFormModal = ({
  isOpen,
  onClose,
  onSave,
  product,
  mode,
  language,
}) => {
  const dispatch = useDispatch();
  const { governoratesList, areas } = useSelector((state) => state.general);
  const { tagsList } = useSelector((state) => state.workshops);

  const [formData, setFormData] = useState({
    name: "",
    images: [], // { id, file, url, name, size, isPrimary }
    price: "",
    discounted_price: "",
    area_id: "",
    governorate_id: "",
    stock_count: "",
    contact_info: "",
    is_active: true,
    description: "",
    tags: [],
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (product) {
      // تحويل الصور القادمة من السيرفر لشكل {file: null, url, id, isPrimary}
      const images = product.images
        ? (Array.isArray(product.images)
            ? product.images
            : [product.images]
          ).map((url, idx) => ({
            id: Date.now() + idx,
            file: null, // الملفات الأصلية غير موجودة
            url,
            name: url.split("/").pop(),
            size: 0,
            isPrimary: idx === 0,
          }))
        : [];

      setFormData({
        name: product.name || "",
        images,
        price: product.price || "",
        discounted_price: product.discounted_price || "",
        governorate_id: product.governorate_id || "",
        area_id: product.area_id || "",
        stock_count: product.stock_count || "",
        contact_info: product.contact_info || "",
        is_active: product.is_active ?? true,
        description: product.description || "",
        tags: product.tags ? product.tags.map((t) => t.id) : [],
      });
    } else {
      setFormData({
        name: "",
        images: [],
        price: "",
        discounted_price: "",
        area_id: "",
        governorate_id: "",
        stock_count: "",
        contact_info: "",
        is_active: true,
        description: "",
        tags: [],
      });
    }
    setErrors({});
  }, [product, isOpen]);

  const labels = {
    addTitle: { en: "Add New Product", ar: "إضافة منتج جديد" },
    editTitle: { en: "Edit Product", ar: "تعديل المنتج" },
    name: { en: "Product Name", ar: "اسم المنتج" },
    images: { en: "Product Images", ar: "صور المنتج" },
    price: { en: "Price", ar: "السعر" },
    discounted_price: { en: "Discounted Price", ar: "السعر المخفض" },
    gov: { en: "Governorate", ar: "المحافظة" },
    area: { en: "Area", ar: "المنطقة" },
    stock_count: { en: "Stock Count", ar: "عدد المخزون" },
    contact_info: { en: "Contact Info", ar: "معلومات الاتصال" },
    is_active: { en: "Active", ar: "نشط" },
    description: { en: "Description", ar: "الوصف" },
    tags: { en: "Tags", ar: "التصنيفات" },
    cancel: { en: "Cancel", ar: "إلغاء" },
    save: { en: "Save", ar: "حفظ" },
    required: { en: "This field is required", ar: "هذا الحقل مطلوب" },
    invalidPrice: {
      en: "Price must be a positive number",
      ar: "يجب أن يكون السعر رقمًا موجبًا",
    },
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = labels.required[language];
    if (!formData.price || isNaN(formData.price) || Number(formData.price) <= 0)
      newErrors.price = labels.invalidPrice[language];
    if (!formData.area_id) newErrors.area_id = labels.required[language];
    if (
      !formData.stock_count ||
      isNaN(formData.stock_count) ||
      Number(formData.stock_count) < 0
    )
      newErrors.stock_count = labels.required[language];
    if (!formData.description?.trim())
      newErrors.description = labels.required[language];
    if (!formData.tags?.length) newErrors.tags = labels.required[language];
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const submitData = new FormData();
    submitData.append("name", formData.name);
    submitData.append("price", Number(formData.price));
    submitData.append(
      "discounted_price",
      Number(formData.discounted_price || 0),
    );
    submitData.append("area_id", formData.area_id);
    submitData.append("governorate_id", formData.governorate_id);
    submitData.append("stock_count", formData.stock_count);
    submitData.append("contact_info", formData.contact_info || "");
    submitData.append("is_active", formData.is_active ? 1 : 0);
    submitData.append("description", formData.description);

    // tags
    formData.tags.forEach((tagId) => submitData.append("tags[]", tagId));

    // الصور: نرسل الملفات فقط، إذا الصورة جديدة موجودة في formData.images.file
    formData.images.forEach((img, idx) => {
      if (img.file) submitData.append(`images[]`, img.file);
    });

    onSave(submitData);
  };

  useEffect(() => {
    if (formData.governorate_id) {
      dispatch(
        getAreaByGovService({ governorate_id: formData.governorate_id }),
      );
      setFormData((prev) => ({ ...prev, area_id: "" }));
    }
  }, [formData.governorate_id, dispatch]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-xl shadow-elevation-4 border border-border w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">
            {mode === "add"
              ? labels.addTitle[language]
              : labels.editTitle[language]}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted"
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input
            label={labels.name[language]}
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            error={errors.name}
            required
          />

          {/* رفع الصور */}
          <ProductImageUpload
            images={formData.images}
            onChange={(images) => setFormData((prev) => ({ ...prev, images }))}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label={labels.price[language]}
              type="number"
              step="0.01"
              value={formData.price}
              onChange={(e) => handleChange("price", e.target.value)}
              error={errors.price}
              required
            />
            <Input
              label={labels.discounted_price[language]}
              type="number"
              step="0.01"
              value={formData.discounted_price}
              onChange={(e) => handleChange("discounted_price", e.target.value)}
            />
            <Select
              label={labels.gov[language]}
              options={governoratesList?.map((g) => ({
                value: g.id,
                label: g.name,
              }))}
              value={formData.governorate_id}
              onChange={(val) => handleChange("governorate_id", val)}
            />
            <Select
              label={labels.area[language]}
              options={areas?.map((a) => ({ value: a.id, label: a.area_name }))}
              value={formData.area_id}
              onChange={(val) => handleChange("area_id", val)}
            />
            <Input
              label={labels.stock_count[language]}
              type="number"
              value={formData.stock_count}
              onChange={(e) => handleChange("stock_count", e.target.value)}
              error={errors.stock_count}
              required
            />
            <Input
              label={labels.contact_info[language]}
              value={formData.contact_info}
              onChange={(e) => handleChange("contact_info", e.target.value)}
            />
            <Checkbox
              label={labels.is_active[language]}
              checked={formData.is_active}
              onChange={(val) => handleChange("is_active", val)}
            />
            <Select
              label={labels.tags[language]}
              options={tagsList?.map((t) => ({ value: t.id, label: t.name }))}
              value={formData.tags}
              onChange={(val) => handleChange("tags", val)}
              multiple
              searchable
              error={errors.tags}
              required
            />
          </div>

          <div>
            <Textarea
              label={labels.description[language]}
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              error={errors.description}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" fullWidth onClick={onClose}>
              {labels.cancel[language]}
            </Button>
            <Button type="submit" variant="default" fullWidth>
              {labels.save[language]}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductFormModal;
